package org.matthewo.pages.event

import emotion.react.css
import js.coroutines.awaitCancellation
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import io.ktor.client.call.*
import io.ktor.client.plugins.resources.*
import org.matthewo.api.EventCompetition
import org.matthewo.api.MetricsAPI
import org.matthewo.api.PushFollowing
import org.matthewo.api.PushSettings
import org.matthewo.client
import org.matthewo.components.Modal
import org.matthewo.components.SVG
import org.matthewo.components.TeamOption
import org.matthewo.components.TeamSearch
import org.matthewo.routes.API
import org.matthewo.serviceworker.NotAtEventException
import org.matthewo.serviceworker.Notifications
import org.matthewo.serviceworker.Notifications.TestResult
import org.matthewo.styles.BACKGROUND_COLOR
import org.matthewo.styles.FONT_COLOR
import org.matthewo.styles.TITLE_FONT_COLOR
import react.FC
import react.Props
import react.Key
import react.PropsWithClassName
import react.dom.aria.AriaChecked
import react.dom.aria.AriaHasPopup
import react.dom.aria.AriaLive
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h3
import react.dom.html.ReactHTML.li
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.ul
import react.useEffect
import react.useId
import react.useState
import web.cssom.*
import web.html.*

external interface NotificationBellProps : PropsWithClassName {
    var coroutineScope: CoroutineScope
    var year: Int
    var code: String
    var name: String?
}

/**
 * Opens what picks the teams at the event this device is told about: shortly before each of their matches, and when
 * its result is posted. Nothing shows in a browser that can't be sent notifications.
 */
val NotificationBell = FC<NotificationBellProps>("NotificationBell") { props ->
    // null until the server says what this device follows at the event
    var following by useState<PushFollowing>()
    var open by useState(false)

    useEffect(props.year, props.code) {
        following = null
        val job = props.coroutineScope.launch {
            following = try {
                Notifications.following(props.year, props.code) ?: NOTHING_FOLLOWED
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                console.warn("Couldn't check notifications for the event: $e")
                NOTHING_FOLLOWED
            }
        }
        awaitCancellation { job.cancel() }
    }

    if (!Notifications.supported) return@FC

    val any = !following?.teams.isNullOrEmpty()
    val label = if (any) "Notifications are on for teams at this event" else "Notifications for teams at this event"

    button {
        type = ButtonType.button
        ariaLabel = label
        title = label
        ariaHasPopup = AriaHasPopup.dialog
        ariaExpanded = open
        disabled = null == following

        css(props.className) {
            width = 1.8.em
            height = 1.8.em
            padding = 0.3.em
            border = None.none
            borderRadius = 0.9.em
            backgroundColor = Color.transparent
            color = TITLE_FONT_COLOR
            cursor = Cursor.pointer
            fontSize = 1.em
            disabled {
                cursor = Cursor.default
                opacity = number(0.5)
            }
        }

        onClick = { open = true }

        SVG {
            css {
                width = 100.pct
                height = 100.pct
            }
            url = if (any) "/bell-filled.svg" else "/bell.svg"
        }
    }

    NotificationSettings {
        this.coroutineScope = props.coroutineScope
        this.year = props.year
        this.code = props.code
        this.name = props.name
        this.open = open
        this.onClose = { open = false }
        this.following = following ?: NOTHING_FOLLOWED
        this.onFollowingChange = { following = it }
    }
}

private external interface NotificationSettingsProps : NotificationBellProps {
    var open: Boolean
    var onClose: () -> Unit
    var following: PushFollowing
    var onFollowingChange: (PushFollowing) -> Unit
}

private val NotificationSettings = FC<NotificationSettingsProps>("NotificationSettings") { props ->
    // whether a change or a test is on its way to the server, so another isn't started over it. nothing's disabled for
    // it, since a request takes a moment and dimming everything for one only flickers
    var busy by useState(false)
    var testing by useState(false)
    var blocked by useState(Notifications.blocked)
    // the event's teams, fetched the first time the dialog's opened at the event
    var loadedTeams by useState<EventTeams>()
    // what happened to the last change or test, until the dialog's opened again or something else is done
    var status by useState<String>()
    val titleId = useId()
    val choicesId = useId()

    useEffect(props.open) {
        if (props.open) {
            status = null
            blocked = Notifications.blocked
        }
    }

    val eventTeams = loadedTeams?.takeIf { it.year == props.year && it.code == props.code }?.teams
    useEffect(props.open, props.year, props.code) {
        if (!props.open || null != eventTeams) return@useEffect
        val year = props.year
        val code = props.code
        val job = props.coroutineScope.launch {
            try {
                val records = client.get(API.Event.Records(API.Event(year, code))).body<List<EventCompetition<Short, MetricsAPI<Short>?>>>()
                loadedTeams = EventTeams(year, code, records.distinctBy { it.number }.map { TeamOption(it.number, it.name) })
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                // a typed number still follows a team without them
                console.warn("Couldn't get the teams at the event: $e")
            }
        }
        awaitCancellation { job.cancel() }
    }

    // changes what's followed, showing [expected] straight away when it's known, and going back to how it was and
    // saying [failure] if it doesn't work
    fun change(failure: String, expected: PushFollowing? = null, change: suspend () -> PushFollowing?) {
        if (busy) return
        val before = props.following
        busy = true
        status = null
        if (null != expected) props.onFollowingChange(expected)
        props.coroutineScope.launch {
            try {
                val following = change()
                if (null != following) props.onFollowingChange(following)
            } catch (e: CancellationException) {
                throw e
            } catch (e: NotAtEventException) {
                if (null != expected) props.onFollowingChange(before)
                status = "Team ${e.team} isn't at this event."
            } catch (e: Exception) {
                console.warn("Couldn't change notifications for the event: $e")
                if (null != expected) props.onFollowingChange(before)
                status = failure
            } finally {
                busy = false
                blocked = Notifications.blocked
            }
        }
    }

    Modal {
        open = props.open
        onClose = props.onClose
        labelledBy = titleId

        css {
            display = Display.flex
            flexDirection = FlexDirection.column
            gap = 1.em
        }

        h3 {
            id = titleId
            css {
                margin = 0.px
                color = TITLE_FONT_COLOR
            }
            +"Notifications for ${props.name ?: props.code}"
        }

        p {
            css { margin = 0.px }
            +"Follow teams here to be notified about their matches."
        }

        if (blocked) p {
            css { margin = 0.px }
            +"Notifications are blocked for this site, which only this browser's settings can undo."
        }

        if (props.following.teams.isNotEmpty()) ul {
            css {
                margin = 0.px
                padding = 0.px
                listStyle = None.none
                display = Display.flex
                flexDirection = FlexDirection.column
                gap = 0.4.em
            }
            for (followed in props.following.teams) li {
                key = Key(followed.number.toString())
                css {
                    display = Display.flex
                    alignItems = AlignItems.center
                    justifyContent = JustifyContent.spaceBetween
                    gap = 1.em
                }
                span {
                    span {
                        css { fontWeight = FontWeight.bold }
                        +"${followed.number}"
                    }
                    followed.name?.let { +" $it" }
                }
                button {
                    type = ButtonType.button
                    ariaLabel = "Stop following ${followed.number}"
                    title = "Stop following"
                    css {
                        flexShrink = number(0.0)
                        width = 1.8.em
                        height = 1.8.em
                        padding = 0.px
                        border = None.none
                        borderRadius = 0.9.em
                        backgroundColor = Color.transparent
                        color = FONT_COLOR
                        fontSize = 1.em
                        cursor = Cursor.pointer
                        userSelect = None.none
                    }
                    onClick = {
                        change(
                            "Couldn't stop following ${followed.number}. Try again in a bit.",
                            props.following.copy(teams = props.following.teams - followed),
                        ) {
                            Notifications.unfollow(props.year, props.code, followed.number, props.following.settings)
                        }
                    }
                    +"✕"
                }
            }
        }

        TeamSearch {
            css {
                borderRadius = 0.5.em
                backgroundColor = FONT_COLOR
                color = BACKGROUND_COLOR

                "&:focus-within" {
                    borderRadius = array(0.5.em, 0.5.em, 0.px, 0.px)
                }

                "[role=listbox]" {
                    maxHeight = 15.em
                    boxShadow = BoxShadow(0.px, 0.4.em, 0.8.em, rgb(0, 0, 0, 0.5))
                }
            }
            label = "Team to follow"
            placeholder = "Team number or name"
            teams = eventTeams
            disabled = blocked
            submitLabel = "Follow"
            onSelect = { team ->
                if (null != team && props.following.teams.any { it.number == team.number }) {
                    status = "You already follow ${team.number}."
                } else if (null != team) {
                    // asked here rather than in the coroutine, which runs after the pick's over
                    val permission = Notifications.askPermission()
                    change("Couldn't follow ${team.number}. Try again in a bit.") {
                        Notifications.follow(props.year, props.code, team.number, props.following.settings, permission)
                    }
                }
            }
            onNoMatch = { status = "No team at this event matches \"$it\"." }
        }

        div {
            role = AriaRole.group
            ariaLabelledBy = choicesId
            css {
                display = Display.flex
                flexDirection = FlexDirection.column
                gap = 0.4.em
            }

            span {
                id = choicesId
                css { fontWeight = FontWeight.bold }
                +"Notify me"
            }

            val settings = props.following.settings
            for ((text, checked, changed) in [
                Triple("When the qualification schedule is posted", settings.schedule) { on: Boolean -> settings.copy(schedule = on) },
                Triple("About 5 minutes before each match", settings.prematch) { on: Boolean -> settings.copy(prematch = on) },
                Triple("When each result is posted", settings.results) { on: Boolean -> settings.copy(results = on) },
            ]) button {
                key = Key(text)
                type = ButtonType.button
                role = AriaRole.switch
                ariaChecked = if (checked) AriaChecked.`true` else AriaChecked.`false`
                disabled = blocked

                css {
                    display = Display.flex
                    alignItems = AlignItems.center
                    justifyContent = JustifyContent.spaceBetween
                    gap = 1.em
                    padding = 0.px
                    border = None.none
                    backgroundColor = Color.transparent
                    color = FONT_COLOR
                    fontSize = 1.em
                    textAlign = TextAlign.start
                    cursor = Cursor.pointer
                    disabled {
                        cursor = Cursor.default
                        opacity = number(0.5)
                    }
                }

                onClick = {
                    val chosen = props.following.copy(settings = changed(!checked))
                    change("Couldn't change which notifications you get. Try again in a bit.", chosen) {
                        // a device that's never followed a team has nothing to change yet, so its choices wait here
                        // until it does
                        Notifications.changeSettings(props.year, props.code, chosen.settings) ?: chosen
                    }
                }

                span { +text }
                Switch { on = checked }
            }
        }

        button {
            type = ButtonType.button
            disabled = props.following.teams.isEmpty()

            css {
                padding = Padding(0.5.em, 1.em)
                border = Border(1.px, LineStyle.solid, TITLE_FONT_COLOR)
                borderRadius = 0.5.em
                backgroundColor = Color.transparent
                color = FONT_COLOR
                fontSize = 1.em
                cursor = Cursor.pointer
                disabled {
                    cursor = Cursor.default
                    opacity = number(0.5)
                }
            }

            onClick = onClick@{
                if (testing) return@onClick
                testing = true
                status = null
                props.coroutineScope.launch {
                    val result = try {
                        Notifications.test(props.year, props.code)
                    } catch (e: CancellationException) {
                        throw e
                    } catch (e: Exception) {
                        console.warn("Couldn't send a test notification: $e")
                        TestResult.FAILED
                    }
                    status = when (result) {
                        TestResult.SENT -> "Sent. It should show up in a few seconds."
                        TestResult.TOO_SOON -> "One was just sent. Wait a few seconds before sending another."
                        TestResult.NOT_FOLLOWING -> "This device wasn't getting notifications for the event anymore. Follow a team to try again."
                        TestResult.FAILED -> "Couldn't send one. Try again in a bit."
                    }
                    if (TestResult.NOT_FOLLOWING == result) props.onFollowingChange(props.following.copy(teams = []))
                    testing = false
                }
            }

            +"Send a test notification"
        }

        // read out when it changes
        p {
            role = AriaRole.status
            ariaLive = AriaLive.polite
            css {
                margin = 0.px
                minHeight = 1.2.em
                fontSize = 0.9.em
            }
            status?.let { +it }
        }

        div {
            css {
                display = Display.flex
                justifyContent = JustifyContent.flexEnd
            }
            button {
                type = ButtonType.button
                css {
                    padding = Padding(0.4.em, 1.em)
                    border = Border(1.px, LineStyle.solid, FONT_COLOR)
                    borderRadius = 0.5.em
                    backgroundColor = Color.transparent
                    color = FONT_COLOR
                    fontSize = 1.em
                    cursor = Cursor.pointer
                }
                onClick = { props.onClose() }
                +"Close"
            }
        }
    }
}

private external interface SwitchProps : Props {
    var on: Boolean
}

// how a switch looks; the button around it is what's pressed
private val Switch = FC<SwitchProps>("Switch") { props ->
    span {
        ariaHidden = true
        css {
            flexShrink = number(0.0)
            position = Position.relative
            width = 2.4.em
            height = 1.4.em
            borderRadius = 0.7.em
            backgroundColor = if (props.on) TITLE_FONT_COLOR else Color("#555555")
            transition = Transition(PropertyName.backgroundColor, 0.15.s, TransitionTimingFunction.ease)
        }
        span {
            css {
                position = Position.absolute
                top = 0.2.em
                left = if (props.on) 1.2.em else 0.2.em
                width = 1.em
                height = 1.em
                borderRadius = 50.pct
                backgroundColor = if (props.on) BACKGROUND_COLOR else FONT_COLOR
                transition = Transition(PropertyName.left, 0.15.s, TransitionTimingFunction.ease)
            }
        }
    }
}

private data class EventTeams(val year: Int, val code: String, val teams: List<TeamOption>)

// what a device follows before it's followed anything
private val NOTHING_FOLLOWED = PushFollowing([], PushSettings())
